import React, { CSSProperties } from 'react';
import mapboxgl, { AnySourceData, MapboxOptions } from 'mapbox-gl';
import "mapbox-gl/dist/mapbox-gl.css"
import { MapViewport } from './AppContext';
import { Popup } from './Popup';

enum MapMode {
    EXPOSURE = 'exposure',
    VAX = 'vax',
    CASES = 'cases'
}


interface Props {
    // mapStyle: string;
    containerStyle?: CSSProperties;

    // latitude: number;
    // longitude: number;
    // zoom: number;

    popups: {
        latitude: number,
        longitude: number,
        children: React.ReactNode,
        options?: mapboxgl.PopupOptions,
        tipColor?: string
    }[]

    padding?: mapboxgl.PaddingOptions
    bounds?: mapboxgl.LngLatBoundsLike

    filter?: any[] | null
    filterVax?: any[] | null
    onClick?: (e: any) => void;
    onMapMove?: (view: MapViewport) => void;

    sources: {[k: string]: AnySourceData};
    layers: {[k: string]: {
        layer: mapboxgl.AnyLayer;
        before?: string;
        filters?: any[],
        mapMode?: MapMode[],
        group?: string
    }}

    visibleLayers?: {[k: string]: boolean}

    initProps: Partial<MapboxOptions>
}

const defer = () => {
	let res, rej;

	let promise = new Promise((resolve, reject) => {
		res = resolve;
		rej = reject;
	});

	(promise as any).resolve = res;
	(promise as any).reject = rej;

	return promise as any;
}

export class Map extends React.Component<Props> {
    mapContainer?: HTMLDivElement | null;
    map?: mapboxgl.Map;
    geolocateControl?: mapboxgl.GeolocateControl;
    geolocateEnabled = false;
    mapReady = false;
    mapReadyPromise: any = defer();

    touchMoved?: mapboxgl.MapTouchEvent & mapboxgl.EventData;
    touchEnd?: mapboxgl.MapTouchEvent & mapboxgl.EventData;

    hoveredLocationId: { [k: string]: any } = {};

    setControlPadding(padding: mapboxgl.PaddingOptions) {
        if (this.map) {
            const ctrlPositions = (this.map as any)._controlPositions;
            ctrlPositions["bottom-left"].style.bottom = `${padding.bottom}px`;
            ctrlPositions["bottom-left"].style.left = `${padding.left}px`;

            ctrlPositions["top-left"].style.top = `${padding.top}px`;
            ctrlPositions["top-left"].style.left = `${padding.left}px`;

            ctrlPositions["bottom-right"].style.bottom = `${padding.bottom}px`;
            ctrlPositions["bottom-right"].style.right = `${padding.right}px`;

            ctrlPositions["top-right"].style.top = `${padding.top}px`;
            ctrlPositions["top-right"].style.right = `${padding.right}px`;
        }
    }

    componentDidMount() {
        if (this.mapContainer) {
            this.map = new mapboxgl.Map({...this.props.initProps, container: this.mapContainer});
            // {
            //     // container: this.mapContainer,
            //     // accessToken: "pk.eyJ1IjoiY292aWQxOW5lYXJtZSIsImEiOiJja3J3d2h1bHQwa3JpMm9uMXk3NHBiYW9qIn0.JTJqjjADLhfFKjuWkE4t_g",
            //     // style: this.props.mapStyle, // 'mapbox://styles/mapbox/streets-v11',
            //     // center: [this.props.longitude, this.props.latitude],
            //     // bounds: this.props.bounds,
            //     // zoom: this.props.zoom,
            //     // attributionControl: false
            // });

            this.map.addControl(new mapboxgl.AttributionControl({
                compact: true
            }));

            this.map.on('moveend', () => {
                if (this.props.onMapMove && this.map) {
                    this.props.onMapMove({
                        center: this.map.getCenter(),
                        zoom: this.map.getZoom(),
                        bounds: this.map.getBounds()
                    })
                }
            })

            this.map.on('load', () => {
                if (!this.map) {
                    // this should never happen
                    return;
                }

                this.mapReady = true;

                if (this.props.onMapMove && this.map) {
                    this.props.onMapMove({
                        center: this.map.getCenter(),
                        zoom: this.map.getZoom(),
                        bounds: this.map.getBounds()
                    })
                }


                this.geolocateControl = new mapboxgl.GeolocateControl({
                    positionOptions: {
                        enableHighAccuracy: true
                    },
                    trackUserLocation: true
                });

                this.geolocateControl.on('trackuserlocationstart', () => {
                    this.geolocateEnabled = true;
                });

                this.map.on('styleimagemissing', (e) => {
                    const id = e.id; // id of the missing image

                    // console.log(id)
                     
                    // // Check if this missing icon is
                    // // one this function can generate.
                    const prefix = 'circle-';
                    if (!id.includes(prefix)) return;
                     
                    // // Get the color from the id.
                    const parts = id.split('-');
                    if(parts.length !== 3){return;}
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    const [_, active, rgb] = parts;
                    
                    const canvas = document.createElement('canvas');// document.getElementById('canvas');
                    if (canvas && canvas.getContext) {
                        const size = 16;
                        canvas.height = size;
                        canvas.width = size;
                        const ctx = canvas.getContext('2d');
                        if(!ctx){return}

                        ctx.fillStyle = active === 'active' ? "#1d4ed8" : "rgb(194, 200, 202)";
                        ctx.beginPath();
                        ctx.arc(size/2, size/2, size/2, 0, Math.PI * 2, true);
                        ctx.closePath();
                        ctx.fill();

                        ctx.fillStyle = "#FFFFFF";
                        ctx.beginPath();
                        ctx.arc(size/2, size/2, size/2 - 1, 0, Math.PI * 2, true);
                        ctx.closePath();
                        ctx.fill();

                        ctx.fillStyle = rgb;
                        ctx.beginPath();
                        ctx.arc(size/2, size/2, size/2 - 3, 0, Math.PI * 2, true);
                        ctx.closePath();
                        ctx.fill();
                        
                        this.map && this.map.addImage(id, { width: size, height: size, data: ctx.getImageData(0, 0, size, size).data });
                    }
                });

                this.map.addControl(
                    this.geolocateControl,
                    "bottom-right"
                );

                this.map.addControl(new mapboxgl.NavigationControl(), "bottom-right");

                if (this.props.padding) {
                    this.map.setPadding(this.props.padding);
                    this.setControlPadding(this.props.padding);
                }

                for(const sourceId in this.props.sources){
                    if(!this.map.getSource(sourceId)){
                        this.map.addSource(sourceId, this.props.sources[sourceId]);
                        // console.log('Added source '+sourceId)
                    }else{
                        if(this.props.sources[sourceId].type === 'geojson'){
                            (this.map.getSource(sourceId) as mapboxgl.GeoJSONSource).setData((this.props.sources[sourceId] as any).data);
                        }
                    }
                }

                for(const layerId in this.props.layers){
                    if(this.map.getLayer(layerId)){continue;}

                    // console.log('Added layer '+layerId)

                    this.map.addLayer(
                        this.props.layers[layerId].layer,
                        this.props.layers[layerId].before
                    );
                }

                // console.log({
                //     layers: this.props.layers,
                //     sources: this.props.sources,
                //     map: this.map
                // })

                this.bindMouseEvents();

                this.updateFilters();
                this.updateVisibility();

                this.mapReadyPromise.resolve();
            });
            
        }
    }

    bindMouseEvents(){
        if(!this.map){return;}

        const clickOrTouchHandler = (e: (mapboxgl.MapTouchEvent & mapboxgl.EventData) | mapboxgl.MapMouseEvent & mapboxgl.EventData) => {
            const rect = this.map?.getCanvas().getBoundingClientRect();
            if (!rect) { return }

            let pointOrBox: mapboxgl.PointLike | [mapboxgl.PointLike, mapboxgl.PointLike];
            const changedTouches = this.touchEnd?.changedTouches || this.touchEnd?.originalEvent?.changedTouches;

            if (changedTouches) {
                if (changedTouches.length !== 1) { return; }
                const touch = changedTouches[0];

                pointOrBox = [
                    [e.point.x - touch.radiusX, e.point.y - touch.radiusY],
                    [e.point.x + touch.radiusX, e.point.y + touch.radiusY]
                ]

                // isTouch = true;
            } else {
                pointOrBox = e.point;

                // isClick = true;
            }

            if (this.props.onClick && this.map) {
                const features = this.map.queryRenderedFeatures(pointOrBox, { layers: Object.keys(this.props.layers) });
                const featuresById: { [k: string]: any } = {};
                for (const feature of features) {
                    if (feature.id != null) {
                        featuresById[feature.id as string] = feature;
                    }
                }
                this.props.onClick({ ...e, features: Object.values(featuresById) });
            }
        }


        // hover features
        const mouseMove = (source: string) => (e: any) => {
            if (this.map && e.features && e.features.length > 0) {
                if (this.hoveredLocationId[source]) {
                    this.map.setFeatureState(
                        { source, id: this.hoveredLocationId[source] },
                        { hover: false }
                    );
                }
                this.hoveredLocationId[source] = (e.features[0] as any).id;
                this.map.setFeatureState(
                    { source, id: this.hoveredLocationId[source] },
                    { hover: true }
                );
                this.map.getCanvas().style.cursor = 'pointer';
                // console.log('move', this.hoveredLocationId)
            }
        };

        const mouseLeave = (source: string) => () => {
            if (this.map && this.hoveredLocationId[source]) {
                this.map.setFeatureState(
                    { source, id: this.hoveredLocationId[source] },
                    { hover: false }
                );
                this.map.getCanvas().style.cursor = '';
            }
            this.hoveredLocationId[source] = null;
            // console.log('leave', this.hoveredLocationId)
        }

        this.map.on('touchstart', (e: any) => {
            this.touchMoved = e;
            this.touchEnd = undefined;
        });

        this.map.on('touchmove', () => {
            this.touchMoved = undefined;
            this.touchEnd = undefined;
        })
        this.map.on('touchend', (e: any) => {
            this.touchEnd = e;
        })

        this.map.on('click', (e: any) => {
            clickOrTouchHandler(e);
        });


        for(const layer in this.props.layers){
            this.map.on('mousemove', layer, mouseMove((this.props.layers[layer].layer as any).source || 'default'));
            this.map.on('mouseleave', layer, mouseLeave((this.props.layers[layer].layer as any).source || 'default'));
        }
    }

    componentDidUpdate(prevProps: Props) {
        if (prevProps.padding !== this.props.padding) {
            if (this.mapReady && this.map && this.props.padding) {
                this.map.setPadding(this.props.padding);
                this.setControlPadding(this.props.padding);
            }
        }

        if (
            // (prevProps.filter !== this.props.filter) || (prevProps.filterVax !== this.props.filterVax) ||
            Object.entries(prevProps.layers).some(([key, l]) => l.filters !== this.props.layers[key].filters)
        ) {
            this.mapReadyPromise.then(() => {
                this.updateFilters();
            })
        }

        if (
            (prevProps.visibleLayers !== this.props.visibleLayers)
         ) {
            this.mapReadyPromise.then(() => {
                this.updateVisibility();
            })
        }

        // if (
        //     this.props.bounds && prevProps.bounds !== this.props.bounds
        // ) {
        //     if (this.map && this.mapReady) {
        //         this.map.fitBounds(this.props.bounds, { linear: true });
        //     }
        // } else if (
        //     prevProps.latitude !== this.props.latitude ||
        //     prevProps.longitude !== this.props.longitude ||
        //     prevProps.zoom !== this.props.zoom
        // ) {
        //     if (this.map && this.mapReady) {
        //         this.map.easeTo({
        //             center: {
        //                 lat: this.props.latitude,
        //                 lon: this.props.longitude,
        //             },
        //             zoom: this.props.zoom
        //         } as any)
        //     }
        // }

        // if (this.props.alternativeMapZoom && prevProps.alternativeMapZoom !== this.props.alternativeMapZoom) {
        //     // console.log({aletnativeMapZoom: this.props.alternativeMapZoom})
        //     this.mapReadyPromise.then(() => {
        //         if(!this.map){return;}
        //         if ((this.props.alternativeMapZoom as { geolocate: boolean }).geolocate) {
        //             if (!this.geolocateEnabled) {
        //                 this.geolocateControl?.trigger();
        //             }
        //         } else {
        //             this.map.easeTo(this.props.alternativeMapZoom as MapViewport)
        //             // console.log('ease to', this.props.alternativeMapZoom)
        //         }
        //     });
        // }

        if(this.props.sources){
            this.mapReadyPromise.then(() => {
                if(!this.map){return;}

                for(const sourceId in this.props.sources){
                    if(
                        this.props.sources[sourceId] !== prevProps.sources[sourceId] ||
                        (this.props.sources[sourceId] as any)?.data !== (prevProps.sources[sourceId] as any)?.data
                    ){
                        if(this.props.sources[sourceId].type === 'geojson'){
                            (this.map.getSource(sourceId) as mapboxgl.GeoJSONSource).setData((this.props.sources[sourceId] as any).data);
                        }
                    }
                }
            });
        }
    }

    updateVisibility(){
        const layers : {[k: string]: boolean} = {};

        for(const layer in this.props.layers){
            layers[layer] = false;
        }
        // for(let prop in MAP_LAYER_MAPPING){
        //     const enabled : boolean = (this.props as any)[prop];
        //     if(enabled !== undefined){
        //         for(const layer of MAP_LAYER_MAPPING[prop]){
        //             if(!enabled){
        //                 layers[layer] = false;
        //             }else if(enabled && layers[layer] !== false){
        //                 layers[layer] = true;
        //             }else{
        //                 layers[layer] = enabled;
        //             }
        //         }
        //     }else{
        //         for(const layer of MAP_LAYER_MAPPING[prop]){
        //             layers[layer] = false;
        //         }
        //     }
        // }

        // now handle map mode
        // if(this.props.mapMode){
        //     for(const mode in MAP_MODE_MAPPING){
        //         if(mode !== this.props.mapMode){
        //             for(const layer of MAP_MODE_MAPPING[mode as MapMode]){
        //                 layers[layer] = false;
        //             }
        //         }else{
        //             for(const layer of MAP_MODE_MAPPING[mode as MapMode]){
        //                 if(layers[layer] == null){
        //                     layers[layer] = true
        //                 }
        //             }
        //         }
        //     }
        // }

        if(this.props.visibleLayers){
            for(const layer in this.props.visibleLayers){
                if(this.props.visibleLayers[layer] != null){
                    layers[layer] = this.props.visibleLayers[layer];
                }
            }
        }

        if(this.map && this.mapReady){
            for(const layer in layers){
                this.map.setLayoutProperty(layer, 'visibility', layers[layer] ? 'visible' : 'none');
            }
        }
    }

    updateFilters(){
        if(this.map && this.mapReady){
            for(let layer in this.props.layers){
                this.map.setFilter(layer, this.props.layers[layer].filters);
            }
        }
    }

    componentWillUnmount() {
        this.map?.remove();
    }


    render() {
        return (
            <div ref={el => this.mapContainer = el} style={this.props.containerStyle || {}}>
                {this.map && this.props.popups.map((popup, i) => <Popup key={i} map={this.map} {...popup} />)}
            </div>
        )
    }
}